import { ActionIcon, Alert, Badge, Box, Card, Flex, Loader, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { IIssueApi } from "@schemas/issueSchema";
import { IconAlertCircle, IconBuilding, IconEye, IconPlus, IconSearch, IconX } from "@tabler/icons-react";
import React, { useCallback, useMemo, useState } from "react";
import {
  useGetMcbimSpacesQuery,
  usePutProjectByProjectIdIssueConnectedBimSpacesMutation,
} from "../../../../../../../openapi";
import { McTitle } from "../../../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import { selectClickedRoomNumberInModal, selectFloorplanIssueBoardId } from "../../Floorplan/floorplanSelectors";
import { selectMcbimSpacesPickerState } from "../../Floorplan/mcbimSelectors";
import { setClickedRoomNumberInModalInRedux } from "../../Floorplan/floorplanSlice";
import IssueModalRoomViewer from "./IssueModalRoomViewer";

type Props = {
  issue: IIssueApi;
  projectId: string;
  hideTitle?: boolean;
};

export const IssueConnectedBimSpacesSection: React.FC<Props> = ({ issue, projectId, hideTitle = false }) => {
  const dispatch = useAppDispatch();
  const issueBoardId = useAppSelector(selectFloorplanIssueBoardId);
  const clickedRoomNumberInModal = useAppSelector(selectClickedRoomNumberInModal);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");
  const [updateConnectedBimSpaces] = usePutProjectByProjectIdIssueConnectedBimSpacesMutation();
  useGetMcbimSpacesQuery({ projectId }, { skip: hideTitle || projectId == null });

  const selectedSpaces = useMemo(() => issue.connectedBimSpaces ?? [], [issue.connectedBimSpaces]);
  const {
    data: fetchedSpaces,
    selectedSpaces: selectedSpacesWithDisplay,
    filteredSpaces,
    isFetching,
    isError,
    error,
  } = useAppSelector((state) => selectMcbimSpacesPickerState(state, selectedSpaces, search));

  const openPicker = useCallback(() => {
    setShowPicker(true);
  }, []);

  const clickedRoomExistsOnIssue = useMemo(
    () => selectedSpaces.some((space) => space.roomNumber?.trim() === clickedRoomNumberInModal),
    [clickedRoomNumberInModal, selectedSpaces],
  );

  const errorMessage = useMemo(() => {
    return (error as any)?.data?.message ?? (error as any)?.error ?? (error as any)?.message ?? "Ukjent feil";
  }, [error]);

  const normalizeSpacesForPayload = useCallback(
    (spaces: typeof selectedSpaces) =>
      spaces.map((space) => ({
        spaceDbId: space.spaceDbId,
        globalId: space.globalId ?? "",
        roomNumber: space.roomNumber ?? "",
        roomName: space.roomName ?? "",
        floor: space.floor ?? "",
        building: space.building ?? "",
        ifcFile: space.ifcFile ?? "",
        pns: space.pns ?? null,
      })),
    [],
  );

  const handleAddSpace = useCallback(
    async (spaceDbId: string) => {
      const selectedSpace = fetchedSpaces.find((space) => space.spaceDbId === spaceDbId);

      if (selectedSpace == null) {
        return;
      }

      await updateConnectedBimSpaces({
        projectId,
        body: {
          issueId: issue._id,
          connectedBimSpaces: normalizeSpacesForPayload([...selectedSpaces, selectedSpace]),
        },
      });

      setSearch("");
      setShowPicker(false);
    },
    [fetchedSpaces, issue._id, normalizeSpacesForPayload, projectId, selectedSpaces, updateConnectedBimSpaces],
  );

  const handleRemoveSpace = useCallback(
    async (spaceDbId: string) => {
      await updateConnectedBimSpaces({
        projectId,
        body: {
          issueId: issue._id,
          connectedBimSpaces: normalizeSpacesForPayload(
            selectedSpaces.filter((space) => space.spaceDbId !== spaceDbId),
          ),
        },
      });
    },
    [issue._id, normalizeSpacesForPayload, projectId, selectedSpaces, updateConnectedBimSpaces],
  );

  const handleSetClickedRoomInModal = useCallback(
    (roomNumber?: string) => {
      const trimmedRoomNumber = roomNumber?.trim();

      if (!trimmedRoomNumber) {
        return;
      }

      if (issueBoardId == null) return;

      dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: trimmedRoomNumber }));
    },
    [dispatch, issueBoardId],
  );

  const content = (
    <>
      <Flex align="center" gap="xs" wrap="wrap" style={{ minHeight: "2.6rem" }}>
        {selectedSpacesWithDisplay.map((space) =>
          (() => {
            const isClickedRoom = clickedRoomNumberInModal === space.roomNumber?.trim();

            return (
              <Badge
                key={space.spaceDbId}
                leftSection={<IconBuilding size={14} />}
                color={isClickedRoom ? "violet" : undefined}
                variant={isClickedRoom ? "light" : undefined}
                styles={
                  isClickedRoom
                    ? {
                        root: {
                          border: "1px solid var(--mantine-color-violet-4)",
                          background: "var(--mantine-color-violet-0)",
                        },
                      }
                    : undefined
                }
                rightSection={
                  <Flex align="center" gap={3}>
                    <Tooltip label="Vis rom i modal" withArrow>
                      <ActionIcon
                        variant={isClickedRoom ? "filled" : "light"}
                        color="violet"
                        size="xs"
                        disabled={!space.roomNumber?.trim()}
                        style={{
                          border: "1px solid var(--mantine-color-violet-4)",
                          background: isClickedRoom ? "var(--mantine-color-violet-6)" : "var(--mantine-color-violet-0)",
                        }}
                        onClick={() => handleSetClickedRoomInModal(space.roomNumber)}
                      >
                        <IconEye size={13} />
                      </ActionIcon>
                    </Tooltip>

                    <Tooltip label="Fjern rom" withArrow>
                      <ActionIcon
                        variant="transparent"
                        color="gray"
                        size="xs"
                        onClick={() => handleRemoveSpace(space.spaceDbId)}
                      >
                        <IconX size={13} />
                      </ActionIcon>
                    </Tooltip>
                  </Flex>
                }
              >
                {space.displayTitle}
              </Badge>
            );
          })(),
        )}

        <Tooltip label="Sok etter rom fra McBim" withArrow>
          <ActionIcon variant="outline" size="sm" onClick={openPicker} disabled={isFetching}>
            {isFetching ? <Loader size={16} /> : <IconPlus size={16} />}
          </ActionIcon>
        </Tooltip>
      </Flex>

      {showPicker && !isError && (
        <Card mt="xs" p="xs" withBorder radius="md">
          <Stack gap="xs">
            <TextInput
              autoFocus
              size="xs"
              placeholder="Sok pa PNS, romnummer, navn, etasje, bygg eller IFC-fil"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              rightSection={
                <ActionIcon variant="subtle" size="sm" onClick={() => setShowPicker(false)}>
                  <IconX size={14} />
                </ActionIcon>
              }
            />

            <Stack gap={4}>
              {filteredSpaces.length === 0 ? (
                <Text c="dimmed" size="sm">
                  Ingen treff.
                </Text>
              ) : (
                filteredSpaces.map((space) => (
                  <Box
                    key={space.spaceDbId}
                    p="xs"
                    style={{
                      borderRadius: 8,
                      cursor: "pointer",
                      border: "1px solid var(--mantine-color-gray-3)",
                    }}
                    onClick={() => handleAddSpace(space.spaceDbId)}
                  >
                    <Text size="sm" fw={500}>
                      {space.displayTitle}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {space.displaySubtitle}
                    </Text>
                  </Box>
                ))
              )}
            </Stack>
          </Stack>
        </Card>
      )}

      {isError && (
        <Alert mt="xs" color="red" title="Klarte ikke a hente rom" icon={<IconAlertCircle size={16} />}>
          {errorMessage}
        </Alert>
      )}
    </>
  );

  if (hideTitle) {
    return content;
  }

  return (
    <>
      <McTitle title="Rom">{content}</McTitle>
      {clickedRoomExistsOnIssue && (
        <LocalErrorBoundary id="IssueModalRoomViewer">
          <IssueModalRoomViewer />
        </LocalErrorBoundary>
      )}
    </>
  );
};
